import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import OfflineVisitWorkspace from './OfflineVisitWorkspace';
import { OfflineSession } from '../../../services/offline/offlineSession';
import { Offline } from '../../../services/offline/offlineServices';
import { Packets } from '../../../services/offline/packetService';
import { useTwoPane } from '../../../hooks/useTwoPane';
import { useFieldMode } from '../../../hooks/useOfflineDevice';
import type { PatientPacketDto } from '../../../dtos/offline/offline.dto';

export const visitWorkspacePath = (visitId: string, patientId: string) => `/visit/${encodeURIComponent(visitId)}?patientId=${encodeURIComponent(patientId)}`;
export const startWorkspacePath = (patientId: string, shiftId?: string) => `/visit/new?patientId=${encodeURIComponent(patientId)}${shiftId ? `&shiftId=${encodeURIComponent(shiftId)}` : ''}`;

/**
 * Route /visit/:visitId (Phase 16C C6, X6). `?patientId=` names the patient; /visit/new starts a
 * visit (`&shiftId=`). Landscape tablets show today's patients beside the workspace (R-310).
 */
const VisitWorkspaceRoute: React.FC = () => {
  const { visitId: param } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const visitId = param && param !== 'new' ? param : null;
  const [patientId, setPatientId] = useState<string | null>(search.get('patientId'));
  const [missing, setMissing] = useState(false);
  const [packets, setPackets] = useState<PatientPacketDto[]>([]);
  const twoPane = useTwoPane(useFieldMode(true));

  useEffect(() => {
    if (patientId || !visitId) {
      if (!patientId) setMissing(true);
      return;
    }
    if (!OfflineSession.getStore()) { setMissing(true); return; }
    Offline.visits.get(visitId).then((v) => (v ? setPatientId(v.patientId) : setMissing(true))).catch(() => setMissing(true));
  }, [patientId, visitId]);

  useEffect(() => {
    if (!twoPane || !OfflineSession.getStore()) return;
    Packets.list().then(setPackets).catch(() => setPackets([]));
  }, [twoPane]);

  if (missing && !patientId) {
    return (
      <div className="mx-auto max-w-md p-6 text-center text-sm text-gray-700" role="alert">
        This visit could not be found on this device.
        <button type="button" onClick={() => navigate('/dashboard')} className="mt-3 block w-full min-h-[44px] rounded-lg bg-slate-800 font-semibold text-white">Back to pelora</button>
      </div>
    );
  }
  if (!patientId) return <p role="status" className="p-6 text-sm text-gray-600">Opening visit…</p>;

  const workspace = (
    <OfflineVisitWorkspace
      key={`${patientId}:${visitId || search.get('shiftId') || 'new'}`}
      patientId={patientId}
      visitId={visitId}
      shiftId={search.get('shiftId')}
      onExit={() => navigate('/dashboard')}
      onVisitStarted={(id) => navigate(visitWorkspacePath(id, patientId), { replace: true })}
    />
  );
  // Phase 18U U18: with a visit open the chart itself goes side by side; the shift list is for picking a visit
  if (!twoPane || visitId) return workspace;
  return (
    <div className="grid min-h-screen grid-cols-[minmax(280px,1fr)_2fr]" data-testid="two-pane">
      <aside className="border-r border-gray-200 bg-white px-4 py-4">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Today</h2>
        <ul className="space-y-2">
          {packets.flatMap((p) => p.shifts.map((s) => (
            <li key={s.shiftId}>
              <button type="button" onClick={() => navigate(startWorkspacePath(p.patientId, s.shiftId))} className="min-h-[48px] w-full rounded-lg border border-gray-200 px-3 text-left text-sm">
                <span className="block font-medium text-gray-900">{[p.header.preferredName || p.header.firstName, p.header.lastName].filter(Boolean).join(' ') || 'Patient'}</span>
                <span className="block text-xs text-gray-600">{new Date(s.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} – {new Date(s.end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
              </button>
            </li>
          )))}
          {packets.length === 0 && <li className="text-sm text-gray-500">No visits stored on this device.</li>}
        </ul>
      </aside>
      <section className="min-w-0">{workspace}</section>
    </div>
  );
};

export default VisitWorkspaceRoute;
